import { DecimalPipe, I18nPluralPipe, JsonPipe } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { CurrencyHtmlPipe } from 'common-util';
import { ItemPricingModel } from 'products-data';
import { ItemPriceSaleAndFeesInfoButtonViewComponent } from '../../../item-price-sale-and-fees-info-button/views/item-price-sale-and-fees-info-button-view/item-price-sale-and-fees-info-button-view.component';
import { CallForPriceButtonComponent } from '../../../call-for-price-button';

@Component({
  selector: 'ideal-item-price-view',
  imports: [
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    ItemPriceSaleAndFeesInfoButtonViewComponent,
    CallForPriceButtonComponent,
    CurrencyHtmlPipe,
    DecimalPipe,
    I18nPluralPipe,
    JsonPipe,
  ],
  standalone: true,
  templateUrl: './item-price-view.component.html',
  styleUrl: './item-price-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.disable-call-for-price]': 'disableCallForPrice()',
    '[class.disable-sales]': 'disableSales()',
    '[class.disable-regular-price]': 'disableRegularPrice()',
    '[class.disable-units]': 'disableUnits()',
    '[class.disable-sales-notice]': 'disableSalesNotice()',
    '[class.disable-fees]': 'disableFees()',
    '[class.is-sale]': 'isSale()',
    '[class.is-limited-sale]': 'isSaleLimited()',
    '[class.has-fees]': 'hasFees()',
    '[class.has-notice]': 'hasNotice()',
    '[class.notice-disabled]': 'isNoticeDisabled()',
    '[attr.view]': 'viewState()',
    '[attr.layout]': 'layout()',
    '[attr.align]': 'align()',
    '[attr.sizing]': 'sizing()',
    '[style.--digits-over]': 'priceDigits() < 9 ? 0 : priceDigits() - 9',
  },
})
export class ItemPriceViewComponent {
  public readonly pricing = input<ItemPricingModel>();
  public readonly usePricePer = input<
    'sold-in' | 'unit' | 'container' | 'auto' | undefined,
    'sold-in' | 'unit' | 'container' | 'auto' | '' | undefined
  >(undefined, {
    transform: (value) => value || undefined,
  });

  public readonly multiplyBy = input(1);
  public readonly sizing = input<'compact' | 'normal' | 'fixed'>('normal');
  public readonly layout = input<'stacked' | 'inline' | 'horizontal'>('stacked');
  public readonly align = input<'start' | 'end' | 'center'>('end');

  public readonly disableCallForPrice = input(false, { transform: booleanAttribute });
  public readonly disableUnits = input(false, { transform: booleanAttribute });

  public readonly disableSales = input(false, { transform: booleanAttribute });
  public readonly disableRegularPrice = input(false, { transform: booleanAttribute });
  public readonly disableSalesNotice = input(false, { transform: booleanAttribute });

  public readonly disableFees = input(false, { transform: booleanAttribute });

  protected readonly hasPrice = computed(() => this.pricing()?.hasPrice ?? false);

  protected readonly isCallForPrice = computed(
    () => !this.disableCallForPrice() && (this.pricing() ? this.pricing()!.isCallForPricing : false),
  );
  protected readonly isSale = computed(() => !this.disableSales() && (this.pricing()?.isOnSale ?? false));
  protected readonly isSaleLimited = computed(
    () => !this.disableSales() && !this.disableSalesNotice() && (this.pricing()?.isOnSaleLimited ?? false),
  );

  protected readonly fees = computed(() => this.pricing()?.fees ?? []);
  protected readonly hasFees = computed(() => !this.disableFees() && this.fees().length > 0);

  protected readonly hasNotice = computed(() => this.hasFees() || this.isSaleLimited());
  protected readonly isNoticeDisabled = computed(() => (this.disableSales() || this.disableSalesNotice()) && this.disableFees());

  protected readonly viewState = computed(() => {
    if (this.isCallForPrice()) {
      return 'call-for-price';
    } else {
      const usePrice = this.usePricePer();
      const quantitySoldIn = this.pricing()?.quantitySoldIn || 1;
      const quantityPerContainer = this.pricing()?.quantityPerContainer || 1;
      const hasSoldIn = quantitySoldIn > 1;
      const hasContainer = quantityPerContainer > 1;

      if (usePrice !== undefined && usePrice !== 'auto') {
        return usePrice;
      }

      if (usePrice === undefined || usePrice === 'auto') {
        if (hasContainer && hasSoldIn) {
          if (usePrice === 'auto' && quantitySoldIn % quantityPerContainer === 0) {
            return 'container';
          }
          return 'sold-in';
        } else if (hasContainer && usePrice === undefined) {
          return 'unit';
        } else if (hasContainer && quantitySoldIn === quantityPerContainer) {
          return 'container';
        } else if (hasSoldIn) {
          return 'sold-in';
        }
        return 'unit';
      }
      return usePrice;
    }
  });

  protected readonly displayQuantity = computed(() => {
    switch (this.viewState()) {
      case 'sold-in':
        return (this.pricing()?.quantitySoldIn ?? 1) * this.multiplyBy();
      default:
        return this.multiplyBy();
    }
  });

  protected readonly displayContainerQuantity = computed(() => {
    return this.pricing()?.quantityPerContainer ?? 1;
  });

  protected readonly displayUnitLabel = computed(() => {
    return this.pricing()?.unitLabelLocalized ?? {};
  });

  protected readonly displayContainerUnitLabel = computed(() => {
    return this.pricing()?.unitContainerLabelLocalized ?? {};
  });

  protected readonly basePriceRegular = computed(() => {
    switch (this.viewState()) {
      case 'container':
        return this.pricing()?.pricePerContainerRegular ?? 0;
      case 'unit':
        return this.pricing()?.pricePerUnitRegular ?? 0;
      case 'sold-in':
      default:
        return this.pricing()?.pricePerSoldInRegular ?? 0;
    }
  });

  protected readonly displayPriceRegular = computed(() => {
    return this.basePriceRegular() * this.multiplyBy();
  });

  private readonly basePrice = computed(() => {
    if (this.isSale() && this.disableSales()) {
      return this.basePriceRegular();
    }

    switch (this.viewState()) {
      case 'container':
        return this.pricing()?.pricePerContainer ?? 0;
      case 'unit':
        return this.pricing()?.pricePerUnit ?? 0;
      case 'sold-in':
      default:
        return this.pricing()?.pricePerSoldIn ?? 0;
    }
  });

  protected readonly displayPrice = computed(() => {
    return this.basePrice() * this.multiplyBy();
  });

  protected readonly priceDigits = computed(() => Math.floor(this.displayPrice()).toString().length);

  protected readonly sale = computed(() => {
    return this.pricing()?.sale ?? undefined;
  });
}
